import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import SEO from "@/components/app/SEO";
import { api } from "@/lib/api";

// After the user approves the PayPal order, PayPal redirects here with
// `token=<orderId>` and `PayerID=...` in the URL. We MUST call the backend
// capture-order endpoint to validate the real payment before showing success.
// This page is the ONLY place where the payment success screen may appear,
// and it appears ONLY after a backend-confirmed PAYMENT_COMPLETED status.
export function PaymentSuccessPage() {
  const location = useLocation();
  const [state, setState] = useState({ status: "loading", message: null });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // PayPal returns `token` = the orderId; we also accept `orderId` for legacy links
    const orderId = params.get("token") || params.get("orderId");
    if (!orderId) {
      setState({ status: "error", message: "Aucun ordre PayPal à confirmer. Retournez à votre dossier pour retenter le paiement." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/payments/paypal/capture-order", { orderId });
        if (cancelled) return;
        if (data?.status === "PAYMENT_COMPLETED") {
          setState({ status: "success" });
        } else if (data?.status === "PAYMENT_CAPTURE_PENDING") {
          setState({ status: "pending", message: "Votre paiement est en cours de confirmation par PayPal. Vous recevrez un email dès qu'il sera validé." });
        } else {
          setState({ status: "error", message: "Le paiement n'a pas pu être confirmé. Aucun montant définitif n'a été prélevé. Retournez à votre dossier pour retenter." });
        }
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || "Le paiement n'a pas pu être confirmé.";
        setState({ status: "error", message: detail });
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <SEO title="Confirmation du paiement" canonical="/paiement/succes" noindex />
      <div className="card-elevated p-10">
        {state.status === "loading" && (
          <>
            <Loader2 className="h-14 w-14 text-primary mx-auto mb-4 animate-spin" data-testid="pay-loading" />
            <h1 className="text-2xl font-bold tracking-tight mb-2">Confirmation du paiement</h1>
            <p className="text-sm text-muted-foreground">Vérification auprès de PayPal en cours…</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" data-testid="pay-success" />
            <h1 className="text-2xl font-bold tracking-tight mb-2">Paiement confirmé</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Merci. Votre paiement PayPal a bien été enregistré. Vous allez recevoir une confirmation par email et votre dossier passe en préparation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/espace/dossiers">
                <Button className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="pay-success-cases-btn">Voir mes dossiers</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" data-testid="pay-success-home-btn">Retour à l'accueil</Button>
              </Link>
            </div>
          </>
        )}
        {state.status === "pending" && (
          <>
            <AlertTriangle className="h-14 w-14 text-yellow-600 mx-auto mb-4" data-testid="pay-pending" />
            <h1 className="text-2xl font-bold tracking-tight mb-2">Paiement en cours de vérification</h1>
            <p className="text-sm text-muted-foreground mb-6">{state.message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/espace/dossiers">
                <Button className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="pay-pending-cases-btn">Voir mes dossiers</Button>
              </Link>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="h-14 w-14 text-destructive mx-auto mb-4" data-testid="pay-error" />
            <h1 className="text-2xl font-bold tracking-tight mb-2">Paiement non confirmé</h1>
            <p className="text-sm text-muted-foreground mb-6">{state.message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/espace/dossiers">
                <Button variant="outline" data-testid="pay-error-cases-btn">Retour à mes dossiers</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" data-testid="pay-error-help-btn">Besoin d'aide ?</Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <SEO title="Paiement annulé" canonical="/paiement/annule" noindex />
      <div className="card-elevated p-10">
        <XCircle className="h-14 w-14 text-orange-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold tracking-tight mb-2">Paiement annulé</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Vous avez interrompu le paiement PayPal. Aucun montant n'a été débité. Vous pouvez retenter à tout moment depuis votre dossier.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/espace/dossiers">
            <Button className="bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="pay-cancel-cases-btn">Retour à mes dossiers</Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" data-testid="pay-cancel-help-btn">Besoin d'aide ?</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
